import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface ScorecardEvaluatorSwitcherOption {
  stepId: string;
  title: string;
  failCount: number;
}

interface ScorecardEvaluatorSwitcherProps {
  options: ScorecardEvaluatorSwitcherOption[];
  /** `null` represents the "All evaluators" scope. */
  selectedStepId: string | null;
  onSelect: (stepId: string | null) => void;
}

const ALL_EVALUATORS_VALUE = "__all_evaluators__";

const FailCountIndicator = ({ count }: { count: number }) =>
  count > 0 ? (
    <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-medium text-red-600">
      <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
      {count}
    </span>
  ) : null;

/**
 * Persistent scope switcher for the row-browsing views (all evaluators vs. a
 * single evaluator). Acts as the page title for those views -- rendered once
 * by the caller and passed into whichever row list is active, so it never
 * unmounts when switching between "All evaluators" and a specific evaluator.
 * Switching scope should feel like changing a filter, not navigating to a
 * different page.
 */
const ScorecardEvaluatorSwitcher = ({
  options,
  selectedStepId,
  onSelect,
}: ScorecardEvaluatorSwitcherProps) => {
  if (options.length === 0) {
    return null;
  }

  const totalFailCount = options.reduce((sum, option) => sum + option.failCount, 0);
  const selectedOption = options.find(option => option.stepId === selectedStepId);
  const selectedLabel = selectedOption ? selectedOption.title : "All evaluators";

  return (
    <Select
      value={selectedStepId ?? ALL_EVALUATORS_VALUE}
      onValueChange={value => onSelect(value === ALL_EVALUATORS_VALUE ? null : value)}
    >
      <SelectTrigger className="-ml-1 h-auto w-auto max-w-full gap-1.5 rounded-md border-transparent bg-transparent px-1 py-0.5 text-base font-semibold text-foreground shadow-none hover:bg-muted/60 focus:ring-0 focus:ring-offset-0">
        <SelectValue>
          <span className="truncate">{selectedLabel}</span>
        </SelectValue>
      </SelectTrigger>
      <SelectContent align="start">
        <SelectItem value={ALL_EVALUATORS_VALUE}>
          <span className="flex min-w-0 items-center gap-2">
            <span>All evaluators</span>
            <FailCountIndicator count={totalFailCount} />
          </span>
        </SelectItem>
        <SelectSeparator />
        {options.map(option => (
          <SelectItem key={option.stepId} value={option.stepId}>
            <span className="flex min-w-0 items-center gap-2">
              <span className="truncate">{option.title}</span>
              <FailCountIndicator count={option.failCount} />
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default ScorecardEvaluatorSwitcher;
